
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Loader2 } from "lucide-react";
import { useTownList } from "@/hooks/use-town-nursing-homes";

interface CountyAreasSectionProps {
  countyName: string;
  countySlug: string;
}

export function CountyAreasSection({ countyName, countySlug }: CountyAreasSectionProps) {
  const { data: towns = [], isLoading } = useTownList(countySlug);

  if (isLoading) {
    return (
      <div className="my-12 bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading areas in {countyName}…
      </div>
    );
  }

  if (!towns.length) return null;

  return (
    <div className="my-12 bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <h2 className="text-2xl font-semibold mb-4 text-grandpal-green-dark">
        Browse Nursing Homes by Town in {countyName}
      </h2>

      <p className="text-grandpal-gray mb-6">
        Find nursing homes in specific towns and areas of {countyName} to help narrow your search and
        find care closest to family and friends.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {towns.map(({ name, count, slug }) => (
          <Link
            key={slug}
            to={`/nursing-homes/${countySlug}/town/${slug}`}
            className="flex items-center justify-between p-3 rounded-md border border-border hover:bg-grandpal-beige/40 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-grandpal-green group-hover:text-grandpal-green-dark transition-colors shrink-0" />
              <span className="text-gray-700 group-hover:text-gray-900 text-sm font-medium transition-colors">
                {name}
              </span>
            </div>
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5 shrink-0">
              {count}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
